import { useMemo, useState, type WheelEvent } from 'react';
import { Check, ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatarMoeda, formatarQuantidade } from '@/lib/notasFiscaisApi';
import { cn } from '@/lib/utils';
import type { DestinatarioNota, ItemNota, ProdutoRevenda, RegraFiscal } from '@/types/notasFiscais';

const UF_EMITENTE = 'SC';

interface ItensNotaEditorProps {
  itens: ItemNota[];
  onChange: (itens: ItemNota[]) => void;
  produtos: ProdutoRevenda[];
  regras: RegraFiscal[];
  destinatario: DestinatarioNota;
}

export function resolverCfop(regra: RegraFiscal | undefined, d: DestinatarioNota): string {
  if (!regra) return '-';
  if ((d.uf || '').toUpperCase() === UF_EMITENTE) return regra.cfop_interno;
  const indicador = d.tipo_pessoa === 'PF' ? 9 : d.indicador_ie;
  return indicador === 9 ? regra.cfop_interestadual_nao_contribuinte : regra.cfop_interestadual_contribuinte;
}

const UNIDADES_INTEIRAS = ['UN', 'UND', 'UNID', 'PC', 'PCA', 'PCS', 'CX', 'KIT', 'JG', 'PAR', 'CJ'];

function unidadeInteira(unidade: string): boolean {
  return UNIDADES_INTEIRAS.includes(String(unidade || '').trim().toUpperCase());
}

function bloquearRolagem(e: WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

function normalizarBusca(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function filtrarCatalogo(value: string, search: string): number {
  const termo = normalizarBusca(search).trim();
  if (!termo) return 1;
  return normalizarBusca(value).includes(termo) ? 1 : 0;
}

export function totalItem(item: ItemNota): number {
  const bruto = Math.round((item.quantidade || 0) * (item.valor_unitario || 0) * 100) / 100;
  return Math.max(bruto - (item.valor_desconto || 0), 0);
}

function itemDeProduto(produto: ProdutoRevenda, regraPadraoId: string | null): ItemNota {
  return {
    produto_id: produto.id,
    codigo: produto.codigo,
    descricao: produto.descricao,
    ncm: produto.ncm,
    cest: produto.cest,
    origem: produto.origem,
    unidade: produto.unidade,
    quantidade: 1,
    valor_unitario: Number(produto.preco_venda) || 0,
    valor_desconto: 0,
    regra_fiscal_id: produto.regra_fiscal_id || regraPadraoId,
  };
}

export function ItensNotaEditor({ itens, onChange, produtos, regras, destinatario }: ItensNotaEditorProps) {
  const [produtoSelecionado, setProdutoSelecionado] = useState<string>('');
  const [catalogoAberto, setCatalogoAberto] = useState(false);
  const regrasAtivas = regras.filter((r) => r.ativo);
  const regraPadraoId = regrasAtivas.find((r) => r.padrao)?.id ?? regrasAtivas[0]?.id ?? null;
  const produtosPorId = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos]);
  const produtoCatalogo = produtos.find((p) => p.id === produtoSelecionado);
  const usoPorProduto = useMemo(() => {
    const uso = new Map<string, number>();
    for (const item of itens) {
      if (!item.produto_id) continue;
      uso.set(item.produto_id, (uso.get(item.produto_id) || 0) + (Number(item.quantidade) || 0));
    }
    return uso;
  }, [itens]);

  const atualizar = (idx: number, campos: Partial<ItemNota>) =>
    onChange(itens.map((item, i) => (i === idx ? { ...item, ...campos } : item)));

  const adicionarProduto = () => {
    const produto = produtos.find((p) => p.id === produtoSelecionado);
    if (!produto) return;
    onChange([...itens, itemDeProduto(produto, regraPadraoId)]);
    setProdutoSelecionado('');
  };

  const placeholderCatalogo = produtos.length === 0
    ? 'Nenhum produto cadastrado'
    : 'Buscar produto pelo nome';

  const adicionarAvulso = () =>
    onChange([
      ...itens,
      {
        produto_id: null,
        codigo: '',
        descricao: '',
        ncm: '',
        cest: null,
        origem: 0,
        unidade: 'UN',
        quantidade: 1,
        valor_unitario: 0,
        valor_desconto: 0,
        regra_fiscal_id: regraPadraoId,
      },
    ]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <Popover modal open={catalogoAberto} onOpenChange={setCatalogoAberto}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={catalogoAberto}
              disabled={produtos.length === 0}
              className="w-full sm:flex-1 justify-between font-normal min-w-0"
            >
              <span className="truncate text-left">
                {produtoCatalogo
                  ? `${produtoCatalogo.codigo} — ${produtoCatalogo.descricao}`
                  : placeholderCatalogo}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="z-[60] w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command filter={filtrarCatalogo}>
              <CommandInput placeholder="Digite o nome do produto" />
              <CommandList>
                <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                <CommandGroup>
                  {produtos.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`${p.codigo} ${p.descricao} ${p.id}`}
                      onSelect={() => {
                        setProdutoSelecionado(p.id);
                        setCatalogoAberto(false);
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4 shrink-0', produtoSelecionado === p.id ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">
                        <span className="font-medium">{p.codigo}</span>
                        <span className="text-muted-foreground">
                          {' '}— {p.descricao} ({formatarMoeda(p.preco_venda)}) — {formatarQuantidade(p.quantidade_estoque, p.unidade)}
                        </span>
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button type="button" onClick={adicionarProduto} disabled={!produtoSelecionado}>
          <Plus className="h-4 w-4 mr-1" /> Adicionar
        </Button>
        <Button type="button" variant="outline" onClick={adicionarAvulso}>
          <Plus className="h-4 w-4 mr-1" /> Item avulso
        </Button>
      </div>

      {itens.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6 border rounded-md">Nenhum item adicionado.</p>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[220px]">Descrição</TableHead>
                <TableHead className="min-w-[120px]">NCM</TableHead>
                <TableHead className="min-w-[110px]">CEST</TableHead>
                <TableHead className="min-w-[80px]">Un.</TableHead>
                <TableHead className="min-w-[130px]">Qtd.</TableHead>
                <TableHead className="min-w-[130px]">Valor un.</TableHead>
                <TableHead className="min-w-[120px]">Desconto</TableHead>
                <TableHead className="min-w-[170px]">Regra fiscal</TableHead>
                <TableHead className="w-[110px] text-right">Total</TableHead>
                <TableHead className="w-[40px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {itens.map((item, idx) => {
                const regra = regrasAtivas.find((r) => r.id === item.regra_fiscal_id);
                const produto = item.produto_id ? produtosPorId.get(item.produto_id) : undefined;
                const usado = item.produto_id ? usoPorProduto.get(item.produto_id) || 0 : 0;
                const estoqueInsuficiente = Boolean(produto && usado > Number(produto.quantidade_estoque));
                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input
                        value={item.descricao}
                        onChange={(e) => atualizar(idx, { descricao: e.target.value })}
                        placeholder="Descrição"
                      />
                      <Input
                        className="mt-1 h-8 text-xs"
                        value={item.codigo}
                        onChange={(e) => atualizar(idx, { codigo: e.target.value })}
                        placeholder="Código"
                      />
                    </TableCell>
                    <TableCell>
                      <Input value={item.ncm} onChange={(e) => atualizar(idx, { ncm: e.target.value })} />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.cest || ''}
                        onChange={(e) => atualizar(idx, { cest: e.target.value || null })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input value={item.unidade} onChange={(e) => atualizar(idx, { unidade: e.target.value })} />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        step={unidadeInteira(item.unidade) ? 1 : 0.01}
                        value={item.quantidade}
                        onWheel={bloquearRolagem}
                        onChange={(e) => {
                          const valor = Number(e.target.value);
                          atualizar(idx, { quantidade: unidadeInteira(item.unidade) ? Math.round(valor) : valor });
                        }}
                      />
                      {produto && (
                        <p className={`text-[11px] mt-1 ${estoqueInsuficiente ? 'text-destructive' : 'text-muted-foreground'}`}>
                          Estoque: {formatarQuantidade(produto.quantidade_estoque, produto.unidade)}
                          {estoqueInsuficiente ? ' · ficará negativo' : ''}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.valor_unitario}
                        onWheel={bloquearRolagem}
                        onChange={(e) => atualizar(idx, { valor_unitario: Number(e.target.value) })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.valor_desconto}
                        onWheel={bloquearRolagem}
                        onChange={(e) => atualizar(idx, { valor_desconto: Number(e.target.value) })}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.regra_fiscal_id || undefined}
                        onValueChange={(v) => atualizar(idx, { regra_fiscal_id: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {regrasAtivas.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        CFOP {resolverCfop(regra, destinatario)} · CSOSN {regra?.csosn ?? '-'}
                      </p>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatarMoeda(totalItem(item))}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onChange(itens.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
